// PropertyForm — the new/edit property sheet, shared by the Clients detail
// panel and the Properties screen.
//
// POST /api/clients/properties  { client_id, name, address, city, state, zip_code, notes }
// PUT  /api/clients/properties/:id  (same fields + is_active)

import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post, put } from '../../shared/http';
import { Sheet, FwField, FwInput, FwSelect, FwTextarea } from '../../../ui/fw';
import AddressField from '../../shared/AddressField';
import { useAction, FormError, FormRow, SheetFooter, pickerNote } from './OfficePage';

const EMPTY = { client_id: '', name: '', address: '', city: '', state: '', zip_code: '', notes: '', is_active: true };

function seed(property, clientId) {
  if (!property) return { ...EMPTY, client_id: clientId ? String(clientId) : '' };
  return {
    client_id: String(property.client_id || clientId || ''),
    name: property.name || '',
    address: property.address || '',
    city: property.city || '',
    state: property.state || '',
    zip_code: property.zip_code || '',
    notes: property.notes || '',
    is_active: property.is_active === undefined ? true : !!property.is_active,
  };
}

/**
 * @param {{ open, property, clientId, clients, clientsError, onClose, onSaved }} props
 *   property     — existing row to edit, or null for a new one
 *   clientId     — preselected client (from the Clients detail panel)
 *   clients      — [{ id, name }] for the client select
 *   clientsError — the error from the roster fetch, when it did not arrive.
 *                  A client is REQUIRED to save, so an empty picker is not a
 *                  cosmetic gap: submit refuses with "Pick the client this
 *                  property belongs to", which tells someone to do a thing the
 *                  form is not offering. The field has to say why instead.
 */
export default function PropertyForm({ open, property, clientId, clients = [], clientsError, onClose, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => seed(property, clientId));
  const { busy, error, run, setError } = useAction();

  useEffect(() => {
    if (open) setForm(seed(property, clientId));
  }, [open, property, clientId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Give the property a name.'); return; }
    if (!form.client_id) { setError('Pick the client this property belongs to.'); return; }
    const body = {
      client_id: parseInt(form.client_id, 10),
      name: form.name.trim(),
      address: form.address,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      notes: form.notes,
    };
    if (property) body.is_active = form.is_active;
    const ok = await run(async () => {
      if (property) await put(`/clients/properties/${property.id}`, body);
      else await post('/clients/properties', body);
      await qc.invalidateQueries({ queryKey: ['properties'] });
      await qc.invalidateQueries({ queryKey: ['clients'] });
      if (property) await qc.invalidateQueries({ queryKey: ['property', property.id] });
    }, 'Could not save the property.');
    if (ok && onSaved) onSaved();
  };

  return (
    <Sheet
      open={open}
      title={property ? 'Edit property' : 'New property'}
      onClose={onClose}
      footer={<SheetFooter onCancel={onClose} busy={busy} formId="or-property-form" submitLabel={property ? 'Save changes' : 'Add property'} />}
    >
      <form id="or-property-form" className="or-form" onSubmit={submit} noValidate>
        <FormError error={error} />
        <FwField
          label="Client"
          required
          htmlFor="or-prop-client"
          error={clientId ? null : pickerNote(clientsError, 'client')}
        >
          <FwSelect value={form.client_id} onChange={set('client_id')} disabled={!!clientId && !property}>
            <option value="">Choose a client</option>
            {clients.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Property name" required htmlFor="or-prop-name">
          <FwInput value={form.name} onChange={set('name')} placeholder="Front lawn, Main house" />
        </FwField>
        {/* AddressField is a plain text input that MAY gain Google suggestions
            when `google_maps_api_key` is set (Office → Settings → Company).
            With no key — the default on a fresh install — it is an ordinary
            FwInput and typing an address works exactly as it did.

            A picked suggestion overwrites city/state/ZIP unconditionally, as
            the classic form does: after choosing a place these three fields
            describe THAT place, and a leftover city from the previous address
            would be wrong while looking right. `street || formatted` because a
            rural place with no street_number/route parses to an empty street
            and blanking the line the user picked would be worse than a long
            one. */}
        <AddressField
          label="Street address"
          htmlFor="or-prop-address"
          value={form.address}
          onChange={set('address')}
          onPlace={(p) => setForm((f) => ({ ...f, address: p.street || p.formatted, city: p.city, state: p.state, zip_code: p.zip }))}
        />
        <FormRow>
          <FwField label="City" htmlFor="or-prop-city">
            <FwInput value={form.city} onChange={set('city')} />
          </FwField>
          <FwField label="State" htmlFor="or-prop-state">
            <FwInput value={form.state} onChange={set('state')} maxLength={2} placeholder="CT" />
          </FwField>
          <FwField label="ZIP" htmlFor="or-prop-zip">
            <FwInput value={form.zip_code} onChange={set('zip_code')} inputMode="numeric" />
          </FwField>
        </FormRow>
        <FwField label="Notes" htmlFor="or-prop-notes">
          <FwTextarea value={form.notes} onChange={set('notes')} rows={3} />
        </FwField>
        {property ? (
          <label className="or-form__check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            Active
          </label>
        ) : null}
      </form>
    </Sheet>
  );
}
